/**
 * Grouping of AngularJS bootstrap failures.
 *
 * A failed boot raises several `$injector` errors within milliseconds, and their messages vary with the module that failed, so one failure would otherwise split into many issues.
 *
 * Drop this module when portal-v2 retires.
 */

import type { EventFilter, TelemetryEvent } from '@ringba/core/telemetry';

const BOOT_FAILURE_MESSAGE = 'V2 failed to start';
const BOOT_FAILURE_FINGERPRINT = 'v2-boot-failure';

const INJECTOR_PREFIX = '[$injector:';
const BOOT_FAILURE_CODE = /^\[\$injector:(?:modulerr|nomod)\]/;
const MISSING_MODULE = /\[\$injector:nomod\] Module '([^']+)' is not available/;

/**
 * Turns the first AngularJS bootstrap failure of a page load into one error, and drops every `$injector` error after it.
 *
 * Rewrites:
 * - The message becomes a fixed one and `error.fingerprint` a fixed key, so Error Tracking groups every occurrence into one issue.
 * - The context gains `missingModule` when the message names the module that was not available.
 *
 * Holds the page load's state, so one instance serves one page load.
 */
export function groupAngularBootFailure<T extends TelemetryEvent>(): EventFilter<T> {
  let hasFailed = false;

  return (event) => {
    if (event.type !== 'error' || !event.error?.message?.startsWith(INJECTOR_PREFIX)) return true;
    if (hasFailed) return false;

    const message = event.error.message;
    if (!BOOT_FAILURE_CODE.test(message)) return true;

    hasFailed = true;
    event.error.message = BOOT_FAILURE_MESSAGE;
    event.error.fingerprint = BOOT_FAILURE_FINGERPRINT;

    const missingModule = MISSING_MODULE.exec(message)?.[1];
    if (missingModule) event.context = { ...event.context, missingModule };
    return true;
  };
}
